import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, Alert, ThemeProvider, CssBaseline, Chip } from '@mui/material';
import { getPlans, PlanType, getSubscribeCurrentPlan } from '../services/subcriptions';
import { theme } from '../theme/theme';
import SubscriptionModal from './SubscriptionModal';
import { useAuth } from '../context/Login.context';
import { Subscription } from '../interfaces/auth.insterface';

const PlanList: React.FC = () => {
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedPlanId, setSelectedPlanId] = useState<number | null>(null);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const { user } = useAuth();

  const handleOpenModal = (planId: number) => {
    setSelectedPlanId(planId);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPlanId(null);
  };

  useEffect(() => {
    const fetchPlansAndSubscription = async () => {
      try {
        const plansData = await getPlans();
        const monthlyPlans = plansData.filter(plan => plan.billing_cycle === 'monthly' || plan.billing_cycle === 'weekly');
        const sortedPlans = monthlyPlans.sort((a, b) => a.id - b.id);
        setPlans(sortedPlans);

        if (user) {
          const subscriptionData = await getSubscribeCurrentPlan();
          setCurrentSubscription(subscriptionData);
        }
      } catch (err) {
        setError('No se pudieron cargar los planes o la suscripción.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlansAndSubscription();
  }, [user]);

  // Filtrar planes para mostrar principales y de prueba
  const mainPlans = plans.filter(plan => !plan.name.toLowerCase().includes('freemium') && !plan.name.toLowerCase().includes('prueba'));
  const freemiumPlans = plans.filter(plan => plan.name.toLowerCase().includes('freemium') || plan.name.toLowerCase().includes('prueba'));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ mt: 4 }}>
        {mainPlans.length > 0 ? (
          <Grid container spacing={4} justifyContent="center">
            {mainPlans.map((plan) => {
              const isCurrentPlan = currentSubscription?.plan?.id === plan.id;
              const featuresArray = plan.description.split('\n').filter(item => item.trim() !== '');
              
              return (
                <Grid item key={plan.id} xs={12} sm={6} md={4}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      borderRadius: '1rem', 
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      opacity: isCurrentPlan ? 0.8 : 1,
                      position: 'relative',
                      transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
                      cursor: isCurrentPlan ? 'default' : 'pointer',
                      backgroundColor: 'white',
                      '&:hover': !isCurrentPlan ? {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 15px 25px -5px rgba(0, 0, 0, 0.15)',
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        '& .selectText': {
                          color: theme.palette.primary.dark,
                          fontWeight: 'bold',
                        }
                      } : {},
                      '&::after': isCurrentPlan ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        pointerEvents: 'none',
                        zIndex: 1,
                        borderRadius: '1rem',
                      } : {},
                    }}
                    onClick={() => !isCurrentPlan && handleOpenModal(plan.id)}
                  >
                    <CardContent sx={{ flexGrow: 1, p: 4, pb: 5 }}>
                      {plan.name === 'Premium' && <Chip label="Popular" color="primary" sx={{ mb: 2 }} />}
                      {isCurrentPlan && <Chip label="Plan Actual" color="success" sx={{ mb: 2 }} />}
                      <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="h4" component="p" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                        ${plan.price} / {plan.billing_cycle === 'monthly' ? 'mes' : plan.billing_cycle === 'weekly' ? 'semana' : 'año'}
                      </Typography>
                      <Box>
                        {featuresArray.map((feature, index) => (
                          <Typography key={index} variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                            ✓ {feature}
                          </Typography>
                        ))}
                      </Box>
                      
                      {!isCurrentPlan && (
                        <Typography 
                          className="selectText"
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.primary.main, 
                            fontWeight: 'bold', 
                            textAlign: 'center', 
                            position: 'absolute', 
                            bottom: 15, 
                            left: 0, 
                            right: 0,
                            transition: 'color 0.2s',
                          }}
                        >
                          Click para seleccionar
                        </Typography>
                      )}
                      {isCurrentPlan && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: theme.palette.success.main, 
                            fontWeight: 'bold', 
                            textAlign: 'center', 
                            position: 'absolute', 
                            bottom: 15, 
                            left: 0, 
                            right: 0 
                          }}
                        >
                          Plan Actual
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}>
            No hay planes disponibles en este momento.
          </Typography>
        )}

        {freemiumPlans.length > 0 && (
          <>
            <Typography
              variant="h5"
              component="h2"
              sx={{
                textAlign: 'center',
                fontWeight: 'bold',
                color: theme.palette.common.white,
                mt: 8,
                mb: 4,
              }}
            >
              Planes de Prueba
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {freemiumPlans.map((plan) => {
                const isCurrentPlan = currentSubscription?.plan?.id === plan.id;
                const featuresArray = plan.description.split('\n').filter(item => item.trim() !== '');
                
                return (
                  <Grid item key={plan.id} xs={12} sm={6} md={4}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        borderRadius: '1rem', 
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        opacity: isCurrentPlan ? 0.8 : 1,
                        position: 'relative',
                        transition: 'transform 0.2s, box-shadow 0.2s, background-color 0.2s',
                        cursor: isCurrentPlan ? 'default' : 'pointer',
                        backgroundColor: 'white',
                        '&:hover': !isCurrentPlan ? {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 15px 25px -5px rgba(0, 0, 0, 0.15)',
                          backgroundColor: 'rgba(255, 255, 255, 0.95)',
                          '& .selectText': {
                            color: theme.palette.primary.dark,
                            fontWeight: 'bold',
                          }
                        } : {},
                        '&::after': isCurrentPlan ? {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.05)',
                          pointerEvents: 'none',
                          zIndex: 1,
                          borderRadius: '1rem',
                        } : {},
                      }}
                      onClick={() => !isCurrentPlan && handleOpenModal(plan.id)}
                    >
                      <CardContent sx={{ flexGrow: 1, p: 4, pb: 5 }}>
                        {isCurrentPlan && <Chip label="Plan Actual" color="success" sx={{ mb: 2 }} />}
                        <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 2 }}>
                          {plan.name}
                        </Typography>
                        <Typography variant="h4" component="p" sx={{ color: theme.palette.primary.main, mb: 2 }}>
                          ${plan.price} / {plan.billing_cycle === 'monthly' ? 'mes' : plan.billing_cycle === 'weekly' ? 'semana' : 'año'}
                        </Typography>
                        <Box>
                          {featuresArray.map((feature, index) => (
                            <Typography key={index} variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
                              ✓ {feature}
                            </Typography>
                          ))}
                        </Box>
                        
                        {!isCurrentPlan && (
                          <Typography 
                            className="selectText"
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.primary.main, 
                              fontWeight: 'bold', 
                              textAlign: 'center', 
                              position: 'absolute', 
                              bottom: 15, 
                              left: 0, 
                              right: 0,
                              transition: 'color 0.2s',
                            }}
                          >
                            Click para seleccionar
                          </Typography>
                        )}
                        {isCurrentPlan && (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: theme.palette.success.main, 
                              fontWeight: 'bold', 
                              textAlign: 'center', 
                              position: 'absolute', 
                              bottom: 15, 
                              left: 0, 
                              right: 0 
                            }}
                          >
                            Plan Actual
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>
      <SubscriptionModal
        open={modalOpen}
        onClose={handleCloseModal}
        planId={selectedPlanId}
        planName={plans.find(p => p.id === selectedPlanId)?.name || ''}
        planBillingCycle={plans.find(p => p.id === selectedPlanId)?.billing_cycle || ''}
      />
    </ThemeProvider>
  );
};

export default PlanList;